function buildNested(path, value, container) {
  const keys = path.replace(/^\//, '').split('/');

  const isIndex = (k) => /^\d+$/.test(k);

  let cur = container;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in cur) || typeof cur[key] !== 'object' || cur[key] === null) {
      cur[key] = {};
    }
    cur = cur[key];

    const nextKey = keys[i + 1];
    if (isIndex(nextKey) && cur['*'] === undefined) {
      cur['*'] = null;
    }
  }

  cur[keys[keys.length - 1]] = value;
}

function coalesceAddRemoveOps(patchOps) {
  const byPath = new Map();
  for (const op of patchOps) {
    if (!byPath.has(op.path)) byPath.set(op.path, []);
    byPath.get(op.path).push(op);
  }

  const result = [];
  for (const [path, ops] of byPath.entries()) {
    const hasAdd = ops.some(o => o.op === 'add');
    const hasRemove = ops.some(o => o.op === 'remove');

    if (hasAdd && hasRemove) {
      const lastAdd = [...ops].reverse().find(o => o.op === 'add');
      result.push({ op: 'replace', path, value: lastAdd.value });
    } else {
      for (const o of ops) result.push(o);
    }
  }
  return result;
}

function jsonDiff(a, b, path = '') {
  // existence checks
  if (a !== undefined && b === undefined) return [{ op: 'remove', path }];
  if (a === undefined && b !== undefined)  return [{ op: 'add', path, value: b }];

  const diffs = [];
  const isPrim = (v) => (v === null || typeof v !== 'object');

  // ---------- OBJECTS ----------
  if (a && b && typeof a === 'object' && typeof b === 'object'
    && !Array.isArray(a) && !Array.isArray(b)) {

    const keysA = new Set(Object.keys(a));
    const keysB = new Set(Object.keys(b));

    // removed fields
    for (const k of keysA) {
      if (!keysB.has(k)) diffs.push({ op: 'remove', path: `${path}/${k}` });
    }

    // added fields
    for (const k of keysB) {
      if (!keysA.has(k)) diffs.push({ op: 'add', path: `${path}/${k}`, value: b[k] });
    }

    // modified fields
    for (const k of keysA) {
      if (!keysB.has(k)) continue;
      const av = a[k], bv = b[k];

      if (isPrim(av) && isPrim(bv)) {
        // if both are primitive values — replace if changed
        if (av !== bv) diffs.push({ op: 'replace', path: `${path}/${k}`, value: bv });
      } else {
        // recursively compare nested objects/arrays
        diffs.push(...jsonDiff(av, bv, `${path}/${k}`));
      }
    }
    return diffs;
  }

  // ---------- ARRAYS ----------
  if (Array.isArray(a) && Array.isArray(b)) {
    const n = a.length, m = b.length;
    const minLen = Math.min(n, m);

    // 1) compare common part by index
    for (let i = 0; i < minLen; i++) {
      diffs.push(...jsonDiff(a[i], b[i], `${path}/${i}`));
    }

    // 2) remove extra items from A
    for (let i = n - 1; i >= m; i--) {
      diffs.push({ op: 'remove', path: `${path}/${i}` });
    }

    // 3) add missing items from B
    for (let i = n; i < m; i++) {
      diffs.push({ op: 'add', path: `${path}/${i}`, value: b[i] });
    }

    return diffs;
  }

  // ---------- PRIMITIVES ----------
  if (isPrim(a) && isPrim(b) && a !== b) {
    diffs.push({ op: 'replace', path, value: b });
  }
  return diffs;
}

function _normArray(arr) {
  const out = {};
  for (let i = 0; i < arr.length; i++) out[String(i)] = _normValue(arr[i]);
  out['*'] = null;
  return out;
}

function _normValue(v) {
  if (Array.isArray(v)) return _normArray(v);
  if (v && typeof v === 'object') {
    const o = {};
    for (const k of Object.keys(v)) o[k] = _normValue(v[k]);
    return o;
  }
  return v;
}

function _normalizePatchArrays(sectionObj) {
  return _normValue(sectionObj);
}

export function generatePatch(a = {}, b = {}) {
  const result = { add: {}, remove: {}, change: {} };
  const rawPatch = jsonDiff(a, b);
  const patch = coalesceAddRemoveOps(rawPatch);

  const addMap = {};
  const removeMap = {};
  const uidAdd = {};
  const uidRemove = {};

  const extractUID = (obj) => (obj && typeof obj === 'object') ? (obj.uid || null) : null;

  for (const op of patch) {
    const parts = op.path.split('/');
    const topKey = parts[1];
    const itemKey = parts.length > 2 ? parts[2] : null;

    if (op.op === 'add') {
      const parts = op.path.split('/');
      const topKey  = parts[1];
      const itemKey = parts.length > 2 ? parts[2] : null;
      const lastSeg = parts[parts.length - 1];
      const isIndex = /^\d+$/.test(lastSeg);

      // Determine if parent and key existed in source "a"
      const parentPath = parts.slice(0, -1).join('/');
      const getAt = (root, p) => {
        const ks = p.replace(/^\//,'').split('/').filter(Boolean);
        let cur = root;
        for (const k of ks) { if (cur == null) return undefined; cur = cur[k]; }
        return cur;
      };
      const parentInA = getAt(a, parentPath);
      const keyExistedInA =
        parentInA &&
        typeof parentInA === 'object' &&
        !Array.isArray(parentInA) &&
        Object.prototype.hasOwnProperty.call(parentInA, lastSeg);

      // Send to CHANGE only when the key already existed at parent (i.e. changing field),
      // otherwise - it's really adding a new key > to ADD.
      const targetBucket = (!isIndex && keyExistedInA) ? result.change : result.add;

      if (Array.isArray(op.value)) {
        buildNested(op.path, undefined, targetBucket);
        const base = op.path.replace(/\/$/, '');
        for (let i = 0; i < op.value.length; i++) {
          buildNested(`${base}/${i}`, op.value[i], targetBucket);
        }
      } else {
        buildNested(op.path, op.value, targetBucket);
      }

      // uid tracking (as before)
      if (topKey && itemKey && typeof op.value === 'object' && !Array.isArray(op.value)) {
        if (!addMap[topKey]) addMap[topKey] = {};
        addMap[topKey][itemKey] = op.value;
        const uid = extractUID(op.value);
        if (uid) uidAdd[uid] = { section: topKey, key: itemKey, value: op.value };
      }
    }

    if (op.op === 'remove') {
      const oldValue = itemKey ? a?.[topKey]?.[itemKey] : a?.[topKey];

      buildNested(op.path, null, result.remove);

      if (oldValue && typeof oldValue === 'object') {
        if (!removeMap[topKey]) removeMap[topKey] = {};
        if (itemKey) removeMap[topKey][itemKey] = oldValue;
        else removeMap[topKey] = oldValue;

        const uid = extractUID(oldValue);
        if (uid) uidRemove[uid] = { section: topKey, key: itemKey, value: oldValue };
      }
    }

    if (op.op === 'replace') {
      if (op.path === '') {
        Object.assign(result.change, op.value);
      } else {
        if (Array.isArray(op.value)) {
          buildNested(op.path, undefined, result.change);
          const base = op.path.replace(/\/$/, '');
          for (let i = 0; i < op.value.length; i++) {
            buildNested(`${base}/${i}`, op.value[i], result.change);
          }
        } else {
          buildNested(op.path, op.value, result.change);
        }
      }
    }
  }

  for (const uid in uidAdd) {
    const added = uidAdd[uid];
    const removed = uidRemove[uid];

    // Merge ONLY if it's exactly the same object in the same place:
    const samePlace =
      removed &&
      removed.section === added.section &&
      removed.key === added.key;

    if (samePlace) {
      // remove them from add/remove and write differences to change
      delete result.add[added.section]?.[added.key];
      delete result.remove[removed.section]?.[removed.key];

      const changes = {};
      const newObj = added.value;
      const oldObj = removed.value;

      for (const k in newObj) {
        if (JSON.stringify(newObj[k]) !== JSON.stringify(oldObj[k])) {
          changes[k] = newObj[k];
        }
      }
      if (Object.keys(changes).length > 0) {
        if (!result.change[added.section]) result.change[added.section] = {};
        result.change[added.section][added.key] = changes;
      }
    }
  }

  // Clean empty objects from result.remove and result.add
  for (const section of ['remove', 'add']) {
    for (const key in result[section]) {
      const value = result[section][key];
      if (value && typeof value === 'object' && Object.keys(value).length === 0) {
        delete result[section][key];
      }
    }
  }

  result.add    = _normalizePatchArrays(result.add);
  result.change = _normalizePatchArrays(result.change);

  return result;
}

/* ============================================================
   APPLY PATCH (Variant A): if there's change.<path>.order —
   FIRST apply order to array, then remove/add/change
   ============================================================ */

const _isIndexKey = (k) => /^\d+$/.test(k);
const _keys = (obj) => Object.keys(obj).filter(k => k !== '*');

function _getAtPath(root, path) {
  const parts = path.replace(/^\//, '').split('/').filter(Boolean);
  let cur = root;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function _ensurePath(root, path) {
  const parts = path.replace(/^\//, '').split('/').filter(Boolean);
  let cur = root;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (cur[p] == null || typeof cur[p] !== 'object') {
      cur[p] = {};
    }
    cur = cur[p];
  }
  return cur;
}

function _applyRemove(obj, path, root) {
  if (!obj || typeof obj !== 'object') return;
  for (const k of _keys(obj)) {
    const v = obj[k];
    const curPath = `${path}/${k}`;

    if (v && typeof v === 'object') {
      // nested removal
      if (_isIndexKey(k)) {
        const parentArr = _getAtPath(root, path);
        if (Array.isArray(parentArr)) {
          const idx = Number(k);
          if (idx >= 0 && idx < parentArr.length) parentArr.splice(idx, 1);
          continue;
        }
      }
      _applyRemove(v, curPath, root);
    } else {
      // v === null → delete property/element
      const parent = _getAtPath(root, path);
      if (Array.isArray(parent) && _isIndexKey(k)) {
        const idx = Number(k);
        if (idx >= 0 && idx < parent.length) parent.splice(idx, 1);
      } else if (parent && typeof parent === 'object') {
        delete parent[k];
      }
    }
  }
}

function _applyAdd(obj, path, root) {
  if (!obj || typeof obj !== 'object') return;
  for (const k of _keys(obj)) {
    const v = obj[k];
    const curPath = `${path}/${k}`;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      _applyAdd(v, curPath, root);
    } else {
      const parent = _getAtPath(root, path);
      if (Array.isArray(parent) && _isIndexKey(k)) {
        parent[Number(k)] = v;
      } else {
        const tgt = _ensurePath(root, path);
        tgt[k] = v;
      }
    }
  }
}

function _applyChange(obj, path, root) {
  if (!obj || typeof obj !== 'object') return;
  for (const k of _keys(obj)) {
    if (k === 'order') continue; // already applied
    const v = obj[k];
    const curPath = `${path}/${k}`;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      _applyChange(v, curPath, root);
    } else {
      const parent = _getAtPath(root, path);
      if (Array.isArray(parent) && _isIndexKey(k)) {
        parent[Number(k)] = v;
      } else {
        const tgt = _ensurePath(root, path);
        tgt[k] = v;
      }
    }
  }
}

export function applyPatch(root, patch) {
  const add    = patch?.add    || {};
  const remove = patch?.remove || {};
  const change = patch?.change || {};

  _applyRemove(remove, '', root);
  _applyAdd(add, '', root);
  _applyChange(change, '', root);

  return root;
}